import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { validatePassword } from '@/lib/validation';
import AuthLayout from '@/layouts/AuthLayout';

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const { signup, signInWithGoogle, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Please enter your name'); return; }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setError(passwordValidation.errors?.join(', ') || 'Password must be at least 8 characters with uppercase, lowercase, number and special character');
      return;
    }

    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (!acceptTerms) { setError('Please accept the terms and conditions'); return; }

    try {
      const result = await signup(name, email, password);
      if (result.success) {
        localStorage.setItem('bravax_has_account', 'true');
        toast({ title: 'Account created!', description: 'Please check your email or sign in to continue.' });

        const searchParams = new URLSearchParams(window.location.search);
        const intentParam = searchParams.get('intent') || sessionStorage.getItem('pending_intent') || '';
        
        if (intentParam) {
          navigate(`/auth/login?intent=${encodeURIComponent(intentParam)}`);
        } else {
          navigate('/auth/login');
        }
      } else {
        setError(result.error || 'Failed to create account. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      if (!result.success) {
        setError(result.error || 'Failed to sign in with Google.');
        setIsGoogleLoading(false);
      }
    } catch {
      setError('Something went wrong with Google sign in.');
      setIsGoogleLoading(false);
    }
  };

  const inputCls = 'h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-primary';

  return (
    <AuthLayout>
      <div className="space-y-7">
        {/* Heading */}
        <div className="space-y-2">
          <h1 className="text-3xl font-display font-medium text-white tracking-normal">Create your account.</h1>
          <p className="text-muted-foreground text-sm">Free forever on the Starter plan. No credit card needed.</p>
        </div>

        {/* Google */}
        <Button
          type="button"
          variant="outline"
          className="w-full h-12 border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl font-medium"
          onClick={handleGoogleSignIn}
          disabled={authLoading || isGoogleLoading}
        >
          {isGoogleLoading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Connecting...</>
          ) : (
            <>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </>
          )}
        </Button>

        <div className="relative">
          <Separator className="bg-white/5" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-3 text-xs text-muted-foreground">
            or sign up with email
          </span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-white/70 text-sm">Full name</Label>
            <Input id="name" type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required disabled={authLoading} className={inputCls} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/70 text-sm">Email</Label>
            <Input id="email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={authLoading} className={inputCls} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/70 text-sm">Password</Label>
            <div className="relative">
              <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Min. 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={authLoading} className={`${inputCls} pr-12`} />
              <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-4 text-white/40 hover:text-white hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-white/70 text-sm">Confirm password</Label>
            <div className="relative">
              <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="Repeat password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required disabled={authLoading} className={`${inputCls} pr-12`} />
              <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-4 text-white/40 hover:text-white hover:bg-transparent" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-3 pt-1">
            <Checkbox id="terms" checked={acceptTerms} onCheckedChange={(c) => setAcceptTerms(!!c)} disabled={authLoading} className="mt-0.5" />
            <Label htmlFor="terms" className="text-sm text-white/60 font-normal leading-relaxed cursor-pointer">
              I agree to the{' '}
              <Link to="#" className="text-primary hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="#" className="text-primary hover:underline">Privacy Policy</Link>
            </Label>
          </div>

          <Button
            type="submit"
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold shadow-[0_0_30px_rgba(0,210,180,0.25)] transition-all hover:scale-[1.01]"
            disabled={authLoading || !acceptTerms || isGoogleLoading}
          >
            {authLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating account...</> : 'Create account'}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to={`/auth/login${window.location.search}`} className="text-primary hover:underline font-medium">Sign in</Link>
        </p>
      </div>
    </AuthLayout>
  );
};

export default Signup;
